// Strip known tracking IDs from the links in a message before it is sent (#311). The composer runs this on the text
// of a new message or a saved edit when the person's `stripTracking` setting is on. The server gets only the clean
// text, so the room never changes a message after it is sent (docs/media.md), and `previewLinks` and the unfurler see
// the clean link with no change of their own.
//
// The rule is per host, because one site's tracking parameter is another site's content: YouTube's `t` is a start
// time, X's `t` is tracking. Each name below is in at least one maintained list (ClearURLs `data.min.json`, AdGuard's
// URL tracking filter, Firefox's strip-on-share lists), checked on 2026-10-28; "observed" means a real link in no list.
// Every client ports this file; the cases are shared/cases/strip-tracking.json.
//
// Out of scope: unwrapping redirect links (`l.instagram.com/?u=`, `l.facebook.com/l.php?u=`) and short links (`t.co`,
// `vm.tiktok.com`, `lnkd.in`). Both need a network request.

/** Stripped on every host. A name ending in `_` is a prefix (`utm_*`). */
export const TRACKING_ANY_HOST: string[] = [
  "utm_",
  "fbclid",
  "gclid",
  "dclid",
  "gbraid",
  "wbraid",
  "msclkid",
  "mc_eid",
  "mc_cid",
  "_hsenc",
  "_hsmi",
  "mkt_tok",
  "yclid",
  "ysclid",
  "twclid",
  "ttclid",
  "li_fat_id",
  "srsltid",
  "oly_enc_id",
];

export interface TrackingHostRule {
  /** The host and, unless `exact`, its subdomains. */
  hosts: string[];
  /** The hosts match only as written (Instagram: `l.instagram.com` and `m.instagram.com` are not these). */
  exact?: boolean;
  /** Stripped on these hosts, besides TRACKING_ANY_HOST. */
  strip: string[];
  /** When set, every parameter is stripped except these (Instagram's share token changes name, so a list falls behind). */
  keepOnly?: string[];
  /** Content, never stripped on these hosts. */
  keep?: string[];
}

export const TRACKING_HOST_RULES: TrackingHostRule[] = [
  { hosts: ["x.com", "twitter.com"], strip: ["s", "t", "ref_src", "src", "refsrc", "ref_url", "cn", "cxt", "a"] },
  { hosts: ["youtube.com", "youtu.be"], strip: ["si", "is", "pp", "feature", "kw"], keep: ["v", "t", "start", "list", "index"] },
  { hosts: ["instagram.com", "www.instagram.com"], exact: true, strip: [], keepOnly: ["img_index", "s"] },
  { hosts: ["threads.net", "threads.com"], strip: ["xmt", "igshid", "igsh"] },
  {
    hosts: ["tiktok.com"],
    strip: ["is_from_webapp", "sender_device", "refer", "tt_from", "_t", "_r", "_d", "u_code", "share_app_name", "share_iid", "user_id", "source"],
  },
  { hosts: ["facebook.com", "fb.watch"], strip: ["mibextid", "rdid", "extid", "__tn__", "ref", "tracking", "referral_code"] },
  { hosts: ["reddit.com"], strip: ["share_id", "correlation_id", "ref_campaign", "ref_source", "ref", "rdt", "$deep_link"], keep: ["context"] },
  { hosts: ["linkedin.com"], strip: ["trk", "trackingid", "refid", "lipi"] },
  { hosts: ["open.spotify.com"], strip: ["si"], keep: ["context"] },
];

/** A redirect link's `u` is the real link, so the whole link stays as it is. */
const UNTOUCHED_HOSTS = ["l.instagram.com"];

/** The host of a link: lower case, no user, no port. */
function hostOf(authority: string): string {
  const at = authority.lastIndexOf("@");
  let host = at >= 0 ? authority.slice(at + 1) : authority;
  if (!host.startsWith("[")) {
    const colon = host.indexOf(":");
    if (colon >= 0) host = host.slice(0, colon);
  }
  return host.toLowerCase();
}

const hostIs = (host: string, domain: string, exact: boolean) => host === domain || (!exact && host.endsWith(`.${domain}`));

function ruleFor(host: string): TrackingHostRule | undefined {
  return TRACKING_HOST_RULES.find((r) => r.hosts.some((d) => hostIs(host, d, r.exact === true)));
}

/**
 * The characters of a tracking parameter's name and value; any other character may be the text after the link. A full
 * stop is not one (#334): it may end a sentence that was typed with no space after it ("?si=b.Next"), and a real ID
 * has none. A parameter that has one stays as typed, so no character of the text is ever lost. The cost: `utm_source=t.co`
 * is not stripped.
 */
const SAFE_NAME = /^[A-Za-z0-9_~%+$-]*$/;
const SAFE_VALUE = /^[A-Za-z0-9_~%+=-]*$/;

/** Whether one query parameter (its name, lower case) is tracking on this host. */
function isTracking(name: string, rule: TrackingHostRule | undefined): boolean {
  if (rule?.keepOnly) return !rule.keepOnly.includes(name);
  if (rule?.keep?.includes(name)) return false;
  if (rule?.strip.includes(name)) return true;
  return TRACKING_ANY_HOST.some((t) => (t.endsWith("_") ? name.startsWith(t) : name === t));
}

/**
 * One http(s) link without its tracking parameters. Everything else stays: the other parameters in their order and
 * spelling, and the fragment. A link with nothing to strip comes back unchanged, character for character.
 */
export function stripTrackingFromLink(link: string): string {
  const scheme = /^[Hh][Tt][Tt][Pp][Ss]?:\/\//.exec(link);
  if (!scheme) return link;
  const afterScheme = scheme[0].length;
  const hash = link.indexOf("#");
  const body = hash >= 0 ? link.slice(0, hash) : link;
  const fragment = hash >= 0 ? link.slice(hash) : "";
  const q = body.indexOf("?", afterScheme);
  if (q < 0) return link;
  const head = body.slice(0, q);
  const authorityEnd = head.slice(afterScheme).search(/\//);
  const authority = authorityEnd < 0 ? head.slice(afterScheme) : head.slice(afterScheme, afterScheme + authorityEnd);
  const host = hostOf(authority);
  if (UNTOUCHED_HOSTS.includes(host)) return link;
  const rule = ruleFor(host);
  const params = body.slice(q + 1).split("&");
  const kept = params.filter((p) => {
    const eq = p.indexOf("=");
    const name = (eq >= 0 ? p.slice(0, eq) : p).toLowerCase();
    // A parameter goes only when it is whole and plain: a value with a bracket, a quote, a comma, a slash or a full stop
    // may run on into text that follows the link, so it stays as typed (#320, #334). A name with no `=` is a word, not
    // an ID ("?s=20&a lot"): every tracking parameter has a value, though the value may be empty.
    const plain = eq >= 0 && SAFE_NAME.test(name) && SAFE_VALUE.test(p.slice(eq + 1));
    return !(plain && isTracking(name, rule));
  });
  if (kept.length === params.length) return link;
  return head + (kept.length ? `?${kept.join("&")}` : "") + fragment;
}

/** Drops sentence punctuation after a link, keeping a closing bracket that has its opening one inside the link. */
function trimTrailing(s: string): string {
  for (;;) {
    const c = s.at(-1);
    if (!c) return s;
    if (".,;:!?'\"*_~|".includes(c)) {
      s = s.slice(0, -1);
      continue;
    }
    const pairs: Record<string, string> = { ")": "(", "]": "[", "}": "{" };
    if (pairs[c]) {
      const opens = s.split(pairs[c]).length - 1;
      const closes = s.split(c).length - 1;
      if (closes > opens) {
        s = s.slice(0, -1);
        continue;
      }
    }
    return s;
  }
}

/** A bare link ends at the first `)` or `]` with no opening one before it in the link: it closes something before the link. */
function endOfBareLink(link: string): number {
  let parens = 0;
  let brackets = 0;
  for (let i = 0; i < link.length; i++) {
    const c = link[i];
    if (c === "(") parens++;
    else if (c === "[") brackets++;
    else if (c === ")" && --parens < 0) return i;
    else if (c === "]" && --brackets < 0) return i;
  }
  return link.length;
}

// Which characters a link is made of is spelled out, because `\s` and `[^\s]` mean different things in JavaScript, ICU
// (Swift) and Java (Kotlin): a link is the printable ASCII characters except `"`, `<`, `>` and the backtick (it starts
// code). The scheme is written `[Hh][Tt][Tt][Pp]`, with no ignore-case flag, because that flag also matches U+017F, U+212A
// (and in Java U+0131, U+0130) against ASCII letters, and JavaScript matches none of them. A letter outside ASCII,
// a no-break space, U+FEFF or U+0085 ends it in every client, so text after a link is never taken for part of it.
const LINK = String.raw`[\x21\x23-\x3B\x3D\x3F-\x5F\x61-\x7E]`;

// Leftmost first: a code block (open to the end of the text if it never closes), inline code, or a link. Code is
// text: a link in it stays as typed. `(?![\s\S])` is the end of the text in every engine, which `$` is not. Groups: 1
// code, 2 its backticks, 3 a link.
const CODE_OR_LINK = new RegExp(
  String.raw`(\`\`\`[\s\S]*?(?:\`\`\`|(?![\s\S]))|(\`+)[\s\S]*?[^\`]\2(?!\`))` +
    String.raw`|([Hh][Tt][Tt][Pp][Ss]?:\/\/${LINK}+)`,
  "g",
);

/**
 * The text with the tracking parameters stripped from each http(s) link in it: bare links and `<links>`. Links in
 * code stay as typed. A character outside the link is never touched. Text with nothing to strip comes back unchanged.
 */
export function stripTracking(text: string): string {
  let out = "";
  let last = 0;
  CODE_OR_LINK.lastIndex = 0;
  for (let m = CODE_OR_LINK.exec(text); m; m = CODE_OR_LINK.exec(text)) {
    const bare = m[3];
    if (bare === undefined) continue; // code: as typed
    const url = trimTrailing(bare.slice(0, endOfBareLink(bare)));
    out += text.slice(last, m.index) + stripTrackingFromLink(url);
    last = m.index + url.length;
    // What follows the link's end may start another link: `[a](https://x?si=1)/[b](https://y?si=2)`.
    CODE_OR_LINK.lastIndex = last;
  }
  return out + text.slice(last);
}
